import {mix, Loadable, Eventable} from '../mixin';
import LoaderAdapter from './LoaderAdapter';

const adapters = {
    loadable: LoaderAdapter
};

class LoaderCollection extends mix().with(Loadable, Eventable)
{
    /** Fügt einen Adapter für einen Loader hinzu. Ein {@link PreloadjsAdapter|Adapter für PreloadJS} ist bereits
     * beispielhaft implementiert.
     * @param {string} key
     * @param {LoaderAdapter} Adapter
     */
    static addAdapter(key, Adapter)
    {
        adapters[key] = Adapter;
    }

    /** Eine LoaderCollection kann den Fortschritt mehrerer Loader tracken und zusammenfassen. Grundsätzlich ist ein Loader
     * ein Objekt, welches das Loadable-Mixin implementiert. Aber über einen {@link PreloadjsAdapter|Adapter} kann
     * jeglicher Loader zur Collection hinzugefügt werden.
     * @mixes Loadable
     * @mixes Eventable
     * @example
     * import LoaderCollection from '@kids/lib/class/LoaderCollection';
     * import AudioController from '@kids/lib/class/AudioController';
     * import ImageLoader from '@kids/lib/class/ImageLoader';
     *
     * const controller = new AudioController();
     * // add audios
     *
     * const loader = new ImageLoader();
     * // add images
     *
     * const collection = new LoaderCollection();
     * collection.add(controller).add(loader);
     * collection.on('load', (evt) =>
     * {
     *     console.log(evt.progress);
     * });
     * collection.on('loaded', (evt) =>
     * {
     *     console.log('loaded');
     * });
     */
    constructor()
    {
        super();
        this._fireLoadEvents = false;
        this._adapters = [];
    }

    getLoaders()
    {
        return this._adapters.map(adapter => adapter.loader);
    }

    /** Fügt einen Loader zur Collection.
     * @param {Object} loader
     * @param {string} [adapterKey="loadable"]
     */
    add(loader, adapterKey = 'loadable')
    {
        if (!adapters.hasOwnProperty(adapterKey))
        {
            throw new ReferenceError('No adapter found for this loader.');
        }

        this.addLoad();
        this._adapters.push(new adapters[adapterKey](loader, this));

        return this;
    }

    /** Hängt alle Events ab und ruft die destroy-Methode der Adapter auf, bevor es alle Adaper entfernt. Ein Laden wird damit
     * unter Umständen nicht aufgehalten. Nur wenn die destroy-Methode eines Adapters den Abbruch implementiert hat.
     */
    destroy()
    {
        this.removeAllListeners();
        while (this._adapters.length)
        {
            this._adapters.pop().destroy();
        }
    }

    /**
     * Setzt den Progress auf null und initialisiert alle Adapater erneut
     */
    reset()
    {
        if (!this.isLoaded()) { return; }
        this.resetLoadProgress();
        this._adapters.forEach((adapter) =>
        {
            this.addLoad();
            adapter.init();
        });
    }

    _updateProgress()
    {
        if (this.isLoaded())
        {
            this.fire('load', {progress: 1});
            this.fire('loaded');
            return;
        }

        let progress = 0;
        const {length} = this._adapters;

        if (length === 0) { return; }

        for (let i = 0; i < length; i++)
        {
            progress += this._adapters[i].getProgress();
        }

        this.fire('load', {progress: progress / length});
    }

    completeLoad(load)
    {
        super.completeLoad(load);
        this._updateProgress();
    }
}

export default LoaderCollection;
